import { aboutBrand, aboutMilestones, aboutCertifications, aboutContact } from '@/mocks/about';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';

export default function AboutPage() {
  const aboutUrl = 'https://qiaojzx.cn/about';

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'qiaojzx.cn',
    url: 'https://qiaojzx.cn',
    description: aboutBrand.description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '浦东新区张江高科技园区博云路2号浦软大厦15层',
      addressLocality: '上海',
      addressRegion: '上海',
      postalCode: '200120',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutContact.phone,
      contactType: 'customer service',
      email: aboutContact.email,
      availableLanguage: ['Chinese'],
      hoursAvailable: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], opens: '09:00', closes: '21:00' },
      ],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qiaojzx.cn' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: aboutUrl },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于 qiaojzx.cn - 一站式综合服务平台',
    description: aboutBrand.description,
    url: aboutUrl,
    isPartOf: { '@type': 'WebSite', name: 'qiaojzx.cn', url: 'https://qiaojzx.cn' },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - qiaojzx.cn 官方平台介绍"
        description={`${aboutBrand.description} ${aboutBrand.vision}`}
        keywords="qiaojzx.cn,关于qiaojzx.cn,qiaojzx.cn 官网入口,qiaojzx.cn 品牌介绍,qiaojzx.cn 联系方式,qiaojzx.cn 信誉"
        canonicalUrl={aboutUrl}
      />
      <JsonLd data={organizationSchema} />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={webpageSchema} />
      <main className="pt-20 pb-16 md:pb-24">
        <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="py-10 md:py-14">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                关于我们
              </span>
              <h1 className="text-2xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">
                {aboutBrand.subtitle}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
                {aboutBrand.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-primary-600 text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">愿景</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">{aboutBrand.vision}</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-flag-line text-accent-600 text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">使命</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">{aboutBrand.mission}</p>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-10 md:py-14 border-t border-background-200/70">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                发展历程
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                一路走来，步步为营
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {aboutMilestones.map((m, idx) => (
                  <div
                    key={m.year}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-10 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 pl-10 md:pl-0 md:px-8">
                      <div className="absolute left-0 md:left-1/2 w-8 h-8 rounded-full bg-primary-500 border-4 border-background-50 flex items-center justify-center md:-translate-x-1/2 z-10">
                        <div className="w-2 h-2 rounded-full bg-background-50"></div>
                      </div>
                      <div className={`p-5 rounded-xl bg-background-50 border border-background-200/70 ${idx % 2 === 0 ? 'md:text-right' : ''}`}>
                        <span className="text-primary-600 font-heading font-bold text-2xl">{m.year}</span>
                        <h3 className="font-heading font-semibold text-foreground-900 text-sm mt-1 mb-2">{m.title}</h3>
                        <p className="text-foreground-500 text-xs leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                    <div className="md:w-1/2 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Certifications */}
          <section className="py-10 md:py-14 border-t border-background-200/70">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                资质认证
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                国际权威认证，值得信赖
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutCertifications.map((cert) => (
                <div
                  key={cert.title}
                  className="flex flex-col items-center text-center p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${cert.icon} text-accent-600 text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">{cert.title}</h3>
                  <p className="text-foreground-500 text-xs">{cert.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 border-t border-background-200/70">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                期待与您沟通
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                如有任何疑问或合作意向，欢迎通过以下方式联系我们。我们的专业团队将在第一时间回复您。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">公司地址</h3>
                    <p className="text-foreground-500 text-xs mt-1">{aboutContact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">客服电话</h3>
                    <p className="text-foreground-500 text-xs mt-1">{aboutContact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-secondary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">电子邮箱</h3>
                    <p className="text-foreground-500 text-xs mt-1">{aboutContact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">工作时间</h3>
                    <p className="text-foreground-500 text-xs mt-1">{aboutContact.workHours}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-900 text-base mb-5">发送消息</h3>
                <form
                  data-readdy-form
                  id="contact-form-about"
                  action="https://readdy.ai/api/form/d9qqugcnlsngrm4lm47g"
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  onSubmit={function(e: React.FormEvent<HTMLFormElement>) {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    const honeyValue = (formData.get('phone_alt') as string || '').trim();
                    const statusEl = document.getElementById('contact-form-status');
                    if (!statusEl) return;
                    if (honeyValue) {
                      statusEl.textContent = '消息已发送，我们会尽快回复！';
                      statusEl.className = 'text-accent-600 text-sm mt-3 text-center';
                      form.reset();
                      return;
                    }
                    statusEl.textContent = '发送中...';
                    statusEl.className = 'text-foreground-400 text-sm mt-3 text-center';
                    const submitBody = new URLSearchParams();
                    formData.forEach((value, key) => {
                      if (key !== 'phone_alt') {
                        submitBody.append(key, value as string);
                      }
                    });
                    fetch('https://readdy.ai/api/form/d9qqugcnlsngrm4lm47g', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: submitBody.toString(),
                    })
                      .then(async (res) => {
                        const text = await res.text();
                        let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
                        try { parsed = JSON.parse(text); } catch { /* ignore */ }
                        return { ok: res.ok, parsed };
                      })
                      .then(({ ok, parsed }) => {
                        if (ok && parsed.code === 'OK') {
                          statusEl.textContent = '感谢您的消息，我们会尽快回复！';
                          statusEl.className = 'text-accent-600 text-sm mt-3 text-center';
                          form.reset();
                        } else {
                          const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
                          statusEl.textContent = serverMsg;
                          statusEl.className = 'text-red-500 text-sm mt-3 text-center';
                        }
                      })
                      .catch(() => {
                        statusEl.textContent = '网络错误，请稍后重试';
                        statusEl.className = 'text-red-500 text-sm mt-3 text-center';
                      });
                  }}
                >
                  <div className="honeypot-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-foreground-600 text-xs font-medium mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-foreground-800 text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-foreground-600 text-xs font-medium mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-foreground-800 text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-foreground-600 text-xs font-medium mb-1.5">主题</label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-foreground-800 text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-foreground-600 text-xs font-medium mb-1.5">消息内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-foreground-800 text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        placeholder="请输入您的消息内容（最多500字）"
                      ></textarea>
                      <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </div>
                  <div id="contact-form-status"></div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}